import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import PinModal from "@/components/RefClick/PinModal";
import { api } from "@/lib/api";
import {
  clearToken,
  emitAuthChange,
  registerPinHandler,
} from "@/lib/auth";

/**
 * AuthContext — one source of truth for "is the app unlocked?".
 *
 * Responsibilities:
 *   - On mount, verify any stored token against /api/auth/status.
 *   - Own the PinModal and expose `promptForPin()` (opens modal, resolves
 *     when the user unlocks, rejects when they cancel).
 *   - Bridge the axios 401 interceptor: any mutating call that returns
 *     401 will call `requestPin()` from lib/auth.js, which dispatches
 *     through here.
 *   - Expose `lock()` to log out.
 *
 * Consumers use `useAuth()` to read `{unlocked, ready, promptForPin, lock}`.
 */

const AuthContext = createContext({
  unlocked: false,
  ready: false,
  pinEnabled: true,
  promptForPin: () => Promise.reject(new Error("Not ready")),
  lock: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export default function AuthProvider({ children }) {
  const [unlocked, setUnlocked] = useState(true);
  // Default to disabled — we only turn the lock UI on if the server
  // EXPLICITLY reports pin_enabled=true. When the auth-status call is
  // in flight or fails (e.g., backend unreachable during a redeploy),
  // we render as if auth is disabled, so no lock icon appears and no
  // action is blocked.
  const [pinEnabled, setPinEnabled] = useState(false);
  const [ready, setReady] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Pending {resolve, reject} for the currently-open modal.
  const pendingRef = useRef(null);

  // Verify the stored token (and read the pin_enabled flag) on first mount.
  // Never throws. When the server reports pin_enabled=false the app is
  // treated as permanently unlocked and the LockChip / PinModal disappear.
  // When the request FAILS (network/5xx/timeout), we also default to
  // "unlocked & lock icon hidden" — a broken /auth/status must never
  // block the user from clicking their own dashboard.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.authStatus();
        if (cancelled) return;
        const enabled = data?.pin_enabled === true;
        setPinEnabled(enabled);
        if (!enabled) {
          // Kill-switch on the server — nothing to unlock.
          clearToken();
          setUnlocked(true);
        } else if (data?.unlocked) {
          setUnlocked(true);
        } else {
          clearToken();
          setUnlocked(false);
        }
      } catch {
        // Server unreachable → keep the app fully accessible. Don't
        // flip pinEnabled=true (that would render the lock icon and
        // block actions on a broken backend). We'll retry next poll.
        if (!cancelled) {
          setPinEnabled(false);
          setUnlocked(true);
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Broadcast unlock changes (LinkDetail listens for password-masking).
  useEffect(() => {
    emitAuthChange(unlocked);
  }, [unlocked]);

  const openModal = useCallback(() => {
    // When the kill-switch is on, nothing needs the modal — resolve
    // immediately so any (defensive) caller doesn't block forever.
    if (!pinEnabled) return Promise.resolve();

    // If a modal is already open, reuse the same pending promise.
    if (pendingRef.current) {
      return new Promise((resolve, reject) => {
        const prev = pendingRef.current;
        pendingRef.current = {
          resolve: (v) => {
            prev.resolve(v);
            resolve(v);
          },
          reject: (e) => {
            prev.reject(e);
            reject(e);
          },
        };
      });
    }
    return new Promise((resolve, reject) => {
      pendingRef.current = { resolve, reject };
      setModalOpen(true);
    });
  }, [pinEnabled]);

  // Register the axios bridge exactly once.
  useEffect(() => {
    return registerPinHandler(() => openModal());
  }, [openModal]);

  const handleSuccess = useCallback(() => {
    setUnlocked(true);
    setModalOpen(false);
    const p = pendingRef.current;
    pendingRef.current = null;
    if (p) p.resolve();
  }, []);

  const handleCancel = useCallback(() => {
    setModalOpen(false);
    const p = pendingRef.current;
    pendingRef.current = null;
    if (p) p.reject(new Error("PIN entry cancelled"));
  }, []);

  const lock = useCallback(() => {
    if (!pinEnabled) return; // no-op with the kill-switch on
    clearToken();
    setUnlocked(false);
    toast.message("Locked", { description: "PIN required for changes." });
  }, [pinEnabled]);

  const value = useMemo(
    () => ({
      unlocked,
      ready,
      pinEnabled,
      promptForPin: openModal,
      lock,
    }),
    [unlocked, ready, pinEnabled, openModal, lock],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {pinEnabled && (
        <PinModal
          open={modalOpen}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      )}
    </AuthContext.Provider>
  );
}
